import { Component, OnInit } from '@angular/core';
import { AuthService } from '../../services/auth/auth.service';
import { NavigationService } from '../../services/shared/navigation.service';
import { MenuesService } from 'src/app/services/menues/menues.service';

import { User } from 'src/app/interfaces/interface-bakend/seguridad/users/user.interface';
import { Menu } from '../../interfaces/interface-bakend/seguridad/acl/menu.interface';
import { DiccionarioKeys } from '@enum/idiomas-diccionario-keys.enum';
import { TiposWebKeys } from '@enum/tiposweb-keys.enum';
import { IdiomaKeys } from '@enum/idiomas-diccionario-keys.enum';

import { StorageService } from 'src/app/services/shared/storage.service';
import { NotificacionesService } from '../../services/notificaciones/notificacionesService';

// ===== Config de caché =====
const MENU_TTL_MS = 24 * 60 * 60 * 1000; // 24 hs
const MENU_CACHE_VERSION = 1;

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent implements OnInit {

    classApplied = false;
    unreadNotificationsCount = 0;

    usuario: User;
    menues: Menu[] = [];
    buildMenu: any[] = [];

    flagMenuLoaded = false;

    // si querés cambiar idioma dinámicamente, podés exponer esta prop
    private lang: IdiomaKeys = IdiomaKeys.ES;
    private lastLoadLang = '';

    ////
    flagUsuarioCargado : boolean;

    ////
    private dropdownTimeouts = new Map<string, any>();
    openDropdown: string | null = null;

    constructor(
        private authservice: AuthService,
        private navigation: NavigationService,
        private menuesService: MenuesService,
        private storage: StorageService,
        private notificacionesService: NotificacionesService
    ) {}

    async ngOnInit(): Promise<void> {
        this.cargarConteoNoLeidos();
        window.addEventListener('unread-messages-updated', () => this.cargarConteoNoLeidos());
        this.cargarMenuPerfil();
        this.cargarUsuario();
    }

    async cargarConteoNoLeidos(): Promise<void> {
        try {
            this.unreadNotificationsCount = await this.notificacionesService.getNoLeidosCount();
        } catch (e) {
            console.error('Error fetching unread count:', e);
        }
    }

    logout() {
        this.authservice.logout();
    }

    toggleClass() {
        this.classApplied = !this.classApplied;
    }

    back(): void {
        this.navigation.back();
    }

    /**
     * Carga el menú con estrategia:
     * 1) Lee de caché y pinta (si hay).
     * 2) Revalida contra backend si está vencido o no hay caché.
     */
    async cargarMenuPerfil() {
        try {
            this.flagMenuLoaded = false;

            // Asegurarnos de tener el usuario cargado primero
            await this.cargarUsuario();
            
            const lang: IdiomaKeys = this.lang;
            this.lastLoadLang = lang;

            // Obtener el perfilId del usuario actual
            const perfilId = this.usuario?.perfil?._id;
            // console.log('🔍 DEBUG - Perfil ID del usuario:', perfilId);
            // console.log('🔍 DEBUG - Usuario completo:', this.usuario);
            
            if (!perfilId) {
                console.warn('Navbar - No se pudo obtener el perfil del usuario');
                this.flagMenuLoaded = true;
                return;
            }

            // 1) Intentar cache con la nueva clave por perfil
            const { data: cached, fresh } =
                this.storage.getMenuCacheWithFreshness(
                    lang,
                    perfilId,
                    MENU_TTL_MS,
                    MENU_CACHE_VERSION
                );

            // console.log('🔍 DEBUG - Cache encontrado:', cached);
            // console.log('🔍 DEBUG - Cache fresh:', fresh);

            // 2) Detectar cache contaminado para usuarios AFILIADO
            if (cached && perfilId === '65dccf7ac186b433577b3aef') {
                // console.log('🔍 DEBUG - Verificando cache contaminado para AFILIADO...');
                const tieneMenuAdministrativo = cached.some(item => 
                    item.menu.name === 'Administración'
                );
                
                // console.log('🔍 DEBUG - ¿Tiene menú administrativo?:', tieneMenuAdministrativo);
                
                if (tieneMenuAdministrativo) {
                    console.warn('❌ Navbar - Cache contaminado detectado para AFILIADO, limpiando...');
                    this.storage.clearMenuCache(lang, perfilId);
                    await this.refreshMenuFromBackendAndCache(lang, perfilId);
                    this.flagMenuLoaded = true;
                    return;
                }
            }

            if (cached) {
                // console.log('🔍 DEBUG - Usando cache válido');
                this.buildMenu = cached;
                this.flagMenuLoaded = true;
                this.analizarEstructuraMenu('CACHE');
            }

            // 3) Si no hay cache o no está fresh => revalidar
            if (!cached || !fresh) {
                // console.log('🔍 DEBUG - No hay cache o no está fresh, recargando desde backend...');
                await this.refreshMenuFromBackendAndCache(lang, perfilId);
            }

        } catch (error) {
            console.error('Navbar - Error al cargar el menú del perfil', error);
            this.flagMenuLoaded = true;
        }
    }

    // Y el método refreshMenuFromBackendAndCache se mantiene igual:
    private async refreshMenuFromBackendAndCache(lang: IdiomaKeys, perfilId: string): Promise<void> {
        try {
            const build = await this.refreshMenuFromBackend(lang);
            
            if (this.lastLoadLang !== lang) return;

            this.storage.setMenuCache(
                lang,
                perfilId,
                build,
                MENU_CACHE_VERSION
            );

            this.buildMenu = build;
            this.flagMenuLoaded = true;
            this.analizarEstructuraMenu('BACKEND');
        } catch (err) {
            console.error('Navbar - Error al refrescar menú desde backend', err);
            throw err;
        }
    }

    private async refreshMenuFromBackend(lang: IdiomaKeys): Promise<any[]> {
        this.usuario = await this.authservice.getUserLogon();

        this.menues = await this.menuesService.findRootNodes(
            TiposWebKeys.WEB,
            DiccionarioKeys.I18NFILE,
            lang
        );

        this.menues.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

        const promises = this.menues.map(async (menu: any) => {
            const descendants =
                await this.menuesService.getFindInmediateDescendants(
                    menu._id,
                    DiccionarioKeys.I18NFILE,
                    lang
                );
            if (Array.isArray(descendants)) {
                descendants.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
            }
            return { menu, descendants };
        });

        return Promise.all(promises);
    }

    // Si en el futuro querés permitir cambiar idioma:
    async useLanguage(newLang: IdiomaKeys) {
        if (this.lang === newLang) return;
        this.lang = newLang;
        await this.cargarMenuPerfil();
    }

    // Helpers de navegación
    go(path: string) {
        this.classApplied = false;
        this.navigation?.back?.(); // opcional si querés colapsar
        // mejor usar router directo si lo tenés inyectado, aquí mantengo como ejemplo
    }

    private async cargarUsuario(): Promise<void> {
        try {
            this.usuario = await this.authservice.getUserLogon();
            console.log('Usuario cargado:', this.usuario);
            this.flagUsuarioCargado = true;
        } catch (error) {
            console.error('Error al cargar usuario:', error);
            this.flagUsuarioCargado = true; // Marcamos como cargado incluso en error
        }
    }

    private analizarEstructuraMenu(fuente: string): void {
        if (!this.buildMenu || this.buildMenu.length === 0) {
            console.log('No hay menú cargado');
            return;
        }

        const estructuraResumida = this.buildMenu.map(item => ({
            menu: {
                nombre: item.menu?.name,
                redirectTo: item.menu?.redirectTo,
                esAdministracion: item.menu?.name
            },
            descendientes: item.descendants?.map((desc: any) => ({
                nombre: desc.name,
                redirectTo: desc.redirectTo,
                icono: desc.icon
            })) || []
        }));

        // console.log('Estructura del menú:', {
        //     totalItems: this.buildMenu.length,
        //     items: estructuraResumida
        // });
    }

    // Métodos mejorados para dropdowns con delays
    onDropdownMouseEnter(menuName: string): void {
        // Cancelar timeout de cierre si existe
        if (this.dropdownTimeouts.has(menuName)) {
            clearTimeout(this.dropdownTimeouts.get(menuName));
            this.dropdownTimeouts.delete(menuName);
        }

        // Abrir dropdown inmediatamente
        this.openDropdown = menuName;
    }

    onDropdownMouseLeave(menuName: string): void {
        // Programar cierre con delay para permitir movimiento entre dropdown y menú
        const timeout = setTimeout(() => {
            if (this.openDropdown === menuName) {
                this.openDropdown = null;
            }
            this.dropdownTimeouts.delete(menuName);
        }, 150); // 150ms de delay

        this.dropdownTimeouts.set(menuName, timeout);
    }

    onDropdownMenuMouseEnter(menuName: string): void {
        // Cuando el mouse entra al dropdown, cancelar cualquier cierre programado
        if (this.dropdownTimeouts.has(menuName)) {
            clearTimeout(this.dropdownTimeouts.get(menuName));
            this.dropdownTimeouts.delete(menuName);
        }
        this.openDropdown = menuName;
    }

    isDropdownOpen(menuName: string): boolean {
        return this.openDropdown === menuName;
    }

    closeAllDropdowns(): void {
        this.openDropdown = null;
        // Limpiar todos los timeouts
        this.dropdownTimeouts.forEach((timeout, key) => {
            clearTimeout(timeout);
        });
        this.dropdownTimeouts.clear();
    }

    /** El Panel Administrativo se dibuja como link simple, aunque tenga hijos. */
    esPanelAdministrativo(item: any): boolean {
        return item?.menu?.redirectTo === '/configurar-administracion';
    }

    getMiCuentaIcon(itemName: string): string {        const iconMap: { [key: string]: string } = {
            'Mis Datos': 'bx bx-user',
            'Mis Solicitudes': 'bx bx-file',
            'Mi Credencial': 'bx bx-id-card',
            'Mis Familiares': 'bx bx-group'
        };
        return iconMap[itemName] || 'bx bx-circle';
    }
}
